import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearToken,
  getToken,
  setToken,
  UNAUTHORIZED_EVENT,
} from "@/auth/storage";

interface AuthContextValue {
  readonly token: string | null;
  readonly lastAuthError: string | null;
  login(token: string): void;
  logout(): void;
  clearAuthError(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [lastAuthError, setLastAuthError] = useState<string | null>(null);

  const login = useCallback(
    (newToken: string) => {
      const trimmed = newToken.trim();
      setToken(trimmed);
      setTokenState(trimmed);
      setLastAuthError(null);
      queryClient.clear();
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setLastAuthError(null);
    queryClient.clear();
  }, [queryClient]);

  const clearAuthError = useCallback(() => {
    setLastAuthError(null);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearToken();
      setTokenState(null);
      setLastAuthError("Token rejected by the server. Please sign in again.");
      queryClient.clear();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ token, lastAuthError, login, logout, clearAuthError }),
    [token, lastAuthError, login, logout, clearAuthError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
